import React, { createContext, useContext, useState, ReactNode } from 'react'

interface FranchiseData {
  id?: number
  fp_code: string
  franchise_name: string
  country: string
  state: string
  last_sync_date?: string
  // Connection flags - already supplied by InspectorLayout's wider
  // FranchiseData shape and consumed by useInspectorAlerts. Surfacing
  // them in the context lets the exception drawer reuse the same
  // useSyncTrigger path as the page-header freshness alert without
  // re-fetching franchise validation data.
  has_complete_lightspeed_connection?: boolean
  has_complete_xero_connection?: boolean
  has_complete_skimmer_connection?: boolean
}

interface InspectorContextType {
  franchiseData: FranchiseData | null
  loading: boolean
  dynamicCrumbOverride: string | null
  setDynamicCrumbOverride: (label: string | null) => void
}

const InspectorContext = createContext<InspectorContextType | undefined>(undefined)

interface InspectorProviderProps {
  children: ReactNode
  franchiseData: FranchiseData | null
  loading: boolean
}

export function InspectorProvider({ children, franchiseData, loading }: InspectorProviderProps) {
  const [dynamicCrumbOverride, setDynamicCrumbOverride] = useState<string | null>(null)

  const contextValue: InspectorContextType = {
    franchiseData,
    loading,
    dynamicCrumbOverride,
    setDynamicCrumbOverride,
  }

  return (
    <InspectorContext.Provider value={contextValue}>
      {children}
    </InspectorContext.Provider>
  )
}

export function useInspector(): InspectorContextType {
  const context = useContext(InspectorContext)
  if (context === undefined) {
    throw new Error('useInspector must be used within an InspectorProvider')
  }
  return context
}

// Helper function to generate page titles
export function generateInspectorTitle(pageTitle?: string, subTitle?: string): string {
  const { franchiseData } = useInspector()
  const franchiseName = franchiseData?.franchise_name || 'Loading...'

  if (!pageTitle) {
    // Dashboard format: "Inspector Tool | {FP_NAME}"
    return `Inspector Tool | ${franchiseName}`
  }

  if (subTitle) {
    // Exception format: "{PAGE_TITLE} - {SUB_TITLE} | {FP_NAME} | Inspector Tool"
    return `${pageTitle} - ${subTitle} | ${franchiseName} | Inspector Tool`
  }

  // Sub-page format: "{PAGE_TITLE} | {FP_NAME} | Inspector Tool"
  return `${pageTitle} | ${franchiseName} | Inspector Tool`
}
